import { type FormikProps, getIn, useFormikContext } from 'formik';
import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import lodashSet from 'lodash/set';
import { useEffect } from 'react';
import { numericFormatter } from 'react-number-format';
import type { SetNonNullable } from 'type-fest';

import { getTenantUnits, getUserInfo } from '@core/configuration';

import type { Address, AddressWireFormat } from '../types/api/Address/Address';
import type { Cost } from '../types/api/Cost/Cost';
import type { CostRule, CostRuleWireFormat } from '../types/api/CostRule/CostRule';
import { Currency, CurrencyWireFormat, NewCurrency } from '../types/api/Currency/Currency';
import type { DeliveryCost } from '../types/api/DeliveryCost/DeliveryCost';
import Enums from '../types/api/Enums';
import {
  IntegrationSummary,
  IntegrationSummaryWireFormat,
} from '../types/api/IntegrationSummary/IntegrationSummary';
import type { QuotePriceEscalation } from '../types/api/QuotePriceEscalation/QuotePriceEscalation';
import { FormReferenceError } from '../types/form';
import { CostString, NullableCostString } from './currency';
import { ZERO_DURATION_STRING, adjustDuration } from './dateHelpers';
import { hasValue } from './equality';
import { resolveCurrencyCode } from './format';
import { logger } from './logger';
import { DEFAULT_COUNTRY, regionFromLocale } from './subdivisions';
import type { NestedKeyOf, NestedKeyOfValue, PathValue } from './types';
import { NIL_UUID } from './uuid';
import type {
  YupSchemaCostType,
  YupSchemaDeliveryCostsType,
  YupSchemaNullableCostType,
  YupSchemaNullableCurrencyType,
  YupSchemaNullableDeliveryCostsType,
  YupSchemaQuotePriceEscalationType,
  YupSchemaReferenceType,
} from './yupHelpers';

/**
 * This file contains Yup <=> Formik validation helper types.
 * They are meant to be used inside of `{Type}Formik` files to reduce
 * overhead if they are a shared type.
 */

/**
 * ISO country code for the current tenant, used as the default for new
 * addresses. UserInfo isn't always loaded (e.g. mobile cold-start), so this
 * stays defensive rather than assuming a tenant is available.
 */
export const tenantCountry = (): string => {
  try {
    return regionFromLocale(getUserInfo().tenant.locale) ?? DEFAULT_COUNTRY;
  } catch {
    return DEFAULT_COUNTRY;
  }
};

export const FormikAddress = (address: Address | undefined): AddressWireFormat => ({
  line1: address?.line1 ?? null,
  line2: address?.line2 ?? null,
  city: address?.city ?? null,
  state: address?.state ?? '',
  postalCode: address?.postalCode ?? null,
  country: address?.country ?? tenantCountry(),
  latitude: address?.latitude ?? null,
  longitude: address?.longitude ?? null,
});

export const FormikNullableIntegrationSummary = (
  summary: IntegrationSummary | undefined | null,
): IntegrationSummaryWireFormat | null => {
  if (summary === null || summary === undefined) return null;
  return {
    id: summary.id,
    name: summary.name,
    integrationType: summary.integrationType,
    externalIdentifier: summary.externalIdentifier,
    isActive: summary.isActive,
    isArchived: summary.isArchived,
    exportQuotes: summary.exportQuotes,
  };
};

/** Translates the `number` string into thousands-friendly format; a blank amount
 * seeds `currencyCode` (the owning entity's currency), else the tenant default. */
export const FormikCurrency = (
  currency: Currency | undefined,
  currencyCode?: string | null,
): CurrencyWireFormat => {
  // A user may start typing a negative value, so consider it empty until a number is entered.
  const numberValue = currency?.number === '-' ? '' : (currency?.number ?? '');
  return {
    number: numericFormatter(numberValue, { thousandSeparator: true }),
    currency: currency?.currency ?? resolveCurrencyCode(currencyCode),
  };
};

/** Helper to translate the `number` string into thousands-friendly format. */
export const FormikNullableCurrency = (
  currency: Currency | null | undefined,
  currencyCode?: string | null,
): YupSchemaNullableCurrencyType =>
  currency === null ? null : FormikCurrency(currency, currencyCode);

/** Helper to translate the `number` string into thousands-friendly format. */
export const FormikCost = (
  cost: Partial<Cost> | undefined,
  currencyCode?: string | null,
): YupSchemaCostType => ({
  amount: FormikCurrency(cost?.amount, currencyCode),
  unit: cost?.unit ?? getTenantUnits().dryVolume,
});

/** Helper to translate the `number` string into thousands-friendly format. */
export const FormikNullableCost = (
  cost: Partial<Cost> | null | undefined,
  currencyCode?: string | null,
): YupSchemaNullableCostType => (cost === null ? null : FormikCost(cost, currencyCode));

export const FormikDeliveryCosts = (
  deliveryCosts: Partial<DeliveryCost> | undefined,
  currencyCode?: string | null,
): SetNonNullable<YupSchemaDeliveryCostsType> => {
  const drivingTimeAdjustmentPercentage = String(
    Number(deliveryCosts?.drivingTimeAdjustmentPercentage ?? '0.000') * 100,
  );
  const unengagedTimeAdjustmentPercentage = String(
    Number(deliveryCosts?.unengagedTimeAdjustmentPercentage ?? '0.000') * 100,
  );
  return {
    deliveryCostOverride:
      deliveryCosts?.deliveryCostOverride === null
        ? {
            amount: {
              number: '',
              currency: resolveCurrencyCode(currencyCode),
            },
            unit: getTenantUnits().dryVolume,
          }
        : FormikCost(deliveryCosts?.deliveryCostOverride, currencyCode),

    perMinCost: FormikCurrency(deliveryCosts?.perMinCost, currencyCode),
    loadTime: deliveryCosts?.loadTime?.toISO() ?? ZERO_DURATION_STRING,
    loadedInYardTime: deliveryCosts?.loadedInYardTime?.toISO() ?? ZERO_DURATION_STRING,
    driveToJobTime: deliveryCosts?.driveToJobTime?.toISO() ?? ZERO_DURATION_STRING,
    waitTime: deliveryCosts?.waitTime?.toISO() ?? ZERO_DURATION_STRING,
    pourTime: deliveryCosts?.pourTime?.toISO() ?? ZERO_DURATION_STRING,
    unloadedAtJobTime: deliveryCosts?.unloadedAtJobTime?.toISO() ?? ZERO_DURATION_STRING,
    driveToPlantTime: deliveryCosts?.driveToPlantTime?.toISO() ?? ZERO_DURATION_STRING,
    drivingTimeAdjustmentPercentage,
    unengagedTimeAdjustmentPercentage,
    fuelVolumeUnit: deliveryCosts?.fuelVolumeUnit ?? getTenantUnits().wetVolume,
    fuelCostPerUnit: FormikCurrency(deliveryCosts?.fuelCostPerUnit, currencyCode),
    driveDistanceUnit: deliveryCosts?.driveDistanceUnit ?? getTenantUnits().length,
    driveDistancePerFuelVolume: deliveryCosts?.driveDistancePerFuelVolume ?? '0',
    driveDistanceToJob: deliveryCosts?.driveDistanceToJob ?? '0',
    driveDistanceToPlant: deliveryCosts?.driveDistanceToPlant ?? '0',
    loadSizeUnit: deliveryCosts?.loadSizeUnit ?? getTenantUnits().dryVolume,
    loadSize: deliveryCosts?.loadSize ?? '1',

    displayOnly: {
      totalDeliveryCost: NullableCostString({ cost: deliveryCosts?.totalDeliveryCost ?? null }),
      adjustedDriveToJobTime: hasValue(deliveryCosts?.driveToJobTime)
        ? adjustDuration(
            deliveryCosts.driveToJobTime,
            deliveryCosts?.drivingTimeAdjustmentPercentage,
          ).toISO()
        : null,
      adjustedDriveToPlantTime: hasValue(deliveryCosts?.driveToPlantTime)
        ? adjustDuration(
            deliveryCosts.driveToPlantTime,
            deliveryCosts?.drivingTimeAdjustmentPercentage,
          ).toISO()
        : null,
    },
  };
};

export const FormikNullableDeliveryCosts = (
  deliveryCosts: Partial<DeliveryCost> | null | undefined,
  currencyCode?: string | null,
): YupSchemaNullableDeliveryCostsType => ({
  deliveryCostOverride:
    deliveryCosts?.deliveryCostOverride === null
      ? null
      : FormikCost(deliveryCosts?.deliveryCostOverride, currencyCode),

  perMinCost: FormikCurrency(deliveryCosts?.perMinCost, currencyCode),
  loadTime: deliveryCosts?.loadTime?.toISO() ?? null,
  loadedInYardTime: deliveryCosts?.loadedInYardTime?.toISO() ?? null,
  driveToJobTime: deliveryCosts?.driveToJobTime?.toISO() ?? null,
  waitTime: deliveryCosts?.waitTime?.toISO() ?? null,
  pourTime: deliveryCosts?.pourTime?.toISO() ?? null,
  unloadedAtJobTime: deliveryCosts?.unloadedAtJobTime?.toISO() ?? null,
  driveToPlantTime: deliveryCosts?.driveToPlantTime?.toISO() ?? null,
  drivingTimeAdjustmentPercentage: deliveryCosts?.drivingTimeAdjustmentPercentage ?? null,
  unengagedTimeAdjustmentPercentage: deliveryCosts?.unengagedTimeAdjustmentPercentage ?? null,
  fuelVolumeUnit: deliveryCosts?.fuelVolumeUnit ?? null,
  fuelCostPerUnit: deliveryCosts?.fuelCostPerUnit ?? null,
  driveDistanceUnit: deliveryCosts?.driveDistanceUnit ?? null,
  driveDistancePerFuelVolume: deliveryCosts?.driveDistancePerFuelVolume ?? null,
  driveDistanceToJob: deliveryCosts?.driveDistanceToJob ?? null,
  driveDistanceToPlant: deliveryCosts?.driveDistanceToPlant ?? null,
  loadSizeUnit: deliveryCosts?.loadSizeUnit ?? null,
  loadSize: deliveryCosts?.loadSize ?? null,

  displayOnly: {
    totalDeliveryCost:
      deliveryCosts?.totalDeliveryCost === undefined
        ? null
        : CostString({ cost: deliveryCosts.totalDeliveryCost }),
    adjustedDriveToJobTime: hasValue(deliveryCosts?.driveToJobTime)
      ? adjustDuration(
          deliveryCosts.driveToJobTime,
          deliveryCosts?.drivingTimeAdjustmentPercentage,
        ).toISO()
      : null,
    adjustedDriveToPlantTime: hasValue(deliveryCosts?.driveToPlantTime)
      ? adjustDuration(
          deliveryCosts.driveToPlantTime,
          deliveryCosts?.drivingTimeAdjustmentPercentage,
        ).toISO()
      : null,
  },
});

/** Helper to format phone number strings from `tel` inputs.
 * The country code and phone number format default to the country code set
 * in the tenant's locale string, but the user can override that by manually
 * inputting the international country calling code beginning with the `+` char.
 */
export const FormikTel = (tel: string | undefined): string => {
  if (!hasValue(tel)) return '';

  let countryCode = '';

  // Resolve country code independently so a missing UserInfo (e.g. mobile cold-start)
  // doesn't prevent parsing an E.164 number that already carries its own country code.
  try {
    countryCode = regionFromLocale(getUserInfo().tenant.locale) ?? '';
  } catch {
    // UserInfo may not be available in all call contexts; fall back to no default country.
  }

  try {
    const parseOptions = countryCode ? { defaultCountry: countryCode as CountryCode } : undefined;
    const phoneNumberInstance = parsePhoneNumberWithError(tel, parseOptions);

    return phoneNumberInstance.formatInternational();
  } catch {
    logger.error('Error parsing phone number %s for countryCode %s', tel, countryCode);
    return tel;
  }
};

export const FormikCostRule = (
  cr: CostRule | null | undefined,
  currencyCode?: string | null,
): CostRuleWireFormat => ({
  kind: cr?.kind ?? Enums.CostRuleKind.Ratio,
  flatChange: FormikNullableCurrency(
    cr?.flatChange ??
      NewCurrency({
        number: '',
        currency: resolveCurrencyCode(currencyCode),
      }),
  ),
  markupRatio:
    cr !== null && cr !== undefined && cr.markupRatio !== null && cr.markupRatio !== ''
      ? String(parseFloat((parseFloat(cr.markupRatio) * 100).toFixed(3)))
      : '',
});

export const QuotePriceEscalationToFormik = (
  qpe: QuotePriceEscalation,
): YupSchemaQuotePriceEscalationType => ({
  escalationDate: qpe.escalationDate.toISO() ?? '',
  changeRatio:
    qpe !== null && qpe !== undefined && qpe.changeRatio !== null && qpe.changeRatio !== ''
      ? String(parseFloat((parseFloat(qpe.changeRatio) * 100).toFixed(3)))
      : '',
  flatChange: qpe.flatChange,
  kind: qpe.kind,
  appliedAt: qpe.appliedAt,
  appliedBy: qpe.appliedBy,
  appliedByName: qpe.appliedByName,
  isProcessed: qpe.isProcessed,
});

export const SetFormikValue = <T extends Record<string, any>, P extends NestedKeyOf<T>>(
  formikBag: FormikProps<T>,
  path: P, // TODO: add support for setting non-leaf properties
  value: PathValue<T, P>,
  shouldValidate?: boolean,
): void => {
  formikBag.setFieldValue(path, value, shouldValidate);
};

export const SetFormikValues = <T extends { [key: string]: any }>(
  formikBag: FormikProps<T>,
  values: Partial<{ [P in NestedKeyOf<T>]: PathValue<T, P> }>,
  shouldValidate?: boolean,
): void => {
  const nextValues = { ...formikBag.values };

  Object.entries(values).forEach(([key, value]) => {
    lodashSet(nextValues, key, value);
  });

  formikBag.setValues(nextValues, shouldValidate);
};

export const ClearFormikField = <T extends Record<string, any>, K extends NestedKeyOf<T>>(
  formikBag: FormikProps<T>,
  emptyFormikObj: T,
  key: K,
): PathValue<T, K> | undefined => {
  const gottenValue = getIn(emptyFormikObj, key);
  if (gottenValue === undefined) {
    logger.warn('Attempted to clear formik value but found no matching key', {
      key,
      availableKeys: Object.keys(formikBag.values),
    });
  }

  // We still allow setting the field;
  // 1. It might still work and formik connections are "incorrect correctly".
  // 2. If it is not connected properly, it won't impact the schema validation.
  SetFormikValue(formikBag, key, gottenValue);
  return gottenValue;
};

/**
 * Heads up, if this function is called while attempting to clear form values
 * at an array-index level, it is suggested to avoid using this. More often than not,
 * initial empty form state values that use arrays will initialize it to empty arrays.
 * That leads to unfound keys, and setting field values to be undefined which may have
 * unexpected results.
 */
export const ClearFormikFields = <T extends Record<string, any>>(
  formikBag: FormikProps<T>,
  emptyFormikObj: NestedKeyOfValue<T, NestedKeyOf<T>>,
  ...keys: NestedKeyOf<T>[]
): any =>
  keys.map((key) => {
    const gottenValue = getIn(emptyFormikObj, key);
    if (gottenValue === undefined) {
      logger.warn('Attempted to clear formik value but found no matching key', {
        key,
        availableKeys: Object.keys(formikBag.values as any),
      });
    }

    // We still allow setting the field;
    // 1. It might still work and formik connections are "incorrect correctly".
    // 2. If it is not connected properly, it won't impact the schema validation.
    SetFormikValue(formikBag, key, gottenValue);
    return gottenValue;
  });

/**
 * FormErrorNotification is used within a <Formik /> context to notify LogRocket
 * if there are validation errors upon form submission.
 */
export const FormErrorNotification = (): null => {
  const { isValid, isValidating, isSubmitting, errors } = useFormikContext();

  useEffect(() => {
    if (!isValid && !isValidating && isSubmitting) {
      logger.warn('Form validation errors when user submits form:', errors);
    }
  }, [isSubmitting, isValid, isValidating]);

  return null;
};

export const hasFormikFieldsErrors = <T extends Record<string, any>, P extends NestedKeyOf<T>>(
  formikBag: FormikProps<T>,
  fieldNames?: P[],
): boolean => {
  if (!hasValue(fieldNames?.length)) {
    return false;
  }

  return fieldNames.some(
    (fieldName) =>
      Boolean(getIn(formikBag.errors, fieldName as unknown as string)) &&
      Boolean(getIn(formikBag.touched, fieldName as unknown as string)),
  );
};

/**
 * Checks if a Formik field at the given path is touched.
 * Handles both boolean touched values and nested object touched states.
 */
export const isFieldTouched = (formikBag: FormikProps<any>, fieldPath: string): boolean => {
  const touched = formikBag.getFieldMeta(fieldPath).touched;
  if (!hasValue(touched)) {
    return false;
  }
  if (typeof touched === 'boolean') {
    return touched;
  }
  return Object.values(touched as Record<string, unknown>).some(Boolean);
};

export const isFormReferenceError = (error: unknown): error is FormReferenceError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'id' in error &&
    typeof (error as { id?: unknown }).id === 'string'
  );
};

export const isFormReference = (value: unknown): value is YupSchemaReferenceType =>
  typeof value === 'object' && hasValue(value) && 'id' in value && 'option' in value;

/** A lookup field's error may be a string, a reference's `{ id }`, or an object of messages. */
export const lookupErrorText = (error: unknown): string | undefined => {
  if (isFormReferenceError(error)) {
    return error.id;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object' && hasValue(error)) {
    const firstError = Object.values(error)[0];
    return typeof firstError === 'string' ? firstError : undefined;
  }
  return undefined;
};

const isFilled = (v: unknown): boolean => {
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim().length > 0 && v !== NIL_UUID;
  if (typeof v === 'number') return !Number.isNaN(v);
  if (typeof v === 'boolean') return v !== undefined && v !== null;
  if (v instanceof Date) return !Number.isNaN(v.getTime());
  if (v && typeof v === 'object') return Object.keys(v).length > 0;
  return v != null;
};

export const hasFormikFieldsFilled = <T extends Record<string, any>, P extends NestedKeyOf<T>>(
  formikBag: FormikProps<T>,
  fieldNames?: P[],
): boolean => {
  if (!fieldNames?.length) return false;

  return fieldNames.every((name) => {
    const value = getIn(formikBag.values, name as unknown as string);

    return isFilled(value);
  });
};

export const getChangedValues = <T extends Record<string, any>>(
  values: T,
  initialValues: T,
): Partial<T> => {
  return Object.keys(values).reduce(
    (acc, key) => {
      if (values[key] !== initialValues[key]) {
        acc[key] = values[key];
      }
      return acc;
    },
    {} as Record<string, any>,
  ) as Partial<T>;
};
